import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import {Shield, Clock, CheckCircle2, XCircle, AlertTriangle, MessageSquare, CreditCard, Instagram, FileText, ChevronDown, Lock, Smartphone, Zap} from 'lucide-react';
import FinalDeAno from "./FinalDeAno";
import DownloadPage from "./Download";

function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".fade-in-section").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToCTA = () => {
    document.getElementById("cta-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const paredSteps = [
  { letter: "P", title: "Pare", description: "Respire fundo antes de qualquer ação", icon: <AlertTriangle className="w-8 h-8" /> },
  { letter: "A", title: "Afaste a urgência", description: "Questione o senso de pressa", icon: <Clock className="w-8 h-8" /> },
  { letter: "R", title: "Revisite a mensagem", description: "Leia com atenção, procure sinais", icon: <MessageSquare className="w-8 h-8" /> },
  { letter: "E", title: "Exija confirmação", description: "Contate por outro canal", icon: <Shield className="w-8 h-8" /> }];


  const features = [
  "O padrão invisível que todo golpe segue",
  "Como usar o teste dos 30 segundos na prática",
  "Os golpes mais comuns no WhatsApp (e como quebrá-los)",
  "Como o Pix é explorado por golpistas",
  "Como identificar lojas falsas e perfis clonados",
  "Como funcionam falsos bancos, atendimentos e boletos",
  "Um checklist final para qualquer situação suspeita",
  "O que fazer se você ou alguém próximo já caiu em golpe"];


  const forWho = [
  "Usa WhatsApp, Pix ou redes sociais",
  "Quer evitar prejuízo financeiro",
  "Quer proteger pais, filhos ou pessoas próximas",
  "Prefere regras claras em vez de explicações longas"];


  const notForWho = [
  "Quem acha que \"isso nunca vai acontecer comigo\"",
  "Quem procura investimento ou ganho fácil",
  "Quem não quer parar 30 segundos antes de agir"];


  const faqs = [
  {
    question: "Isso funciona para qualquer idade?",
    answer: "Sim. O método é simples e funciona especialmente bem para quem não é técnico."
  },
  {
    question: "Serve para pais e avós?",
    answer: "Principalmente para eles. Inclusive é um ótimo material para compartilhar."
  },
  {
    question: "Isso substitui antivírus ou segurança digital?",
    answer: "Não. Ele complementa, cuidando da parte mais explorada: a decisão humana."
  },
  {
    question: "Vou ficar paranoico depois de ler?",
    answer: "Não. O foco é calma, não medo."
  }];


  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-900 via-sky-700 to-cyan-600 opacity-98" />
        <img
          src="https://static.lumi.new/61/610ece517e0402800af13a6815182f24.png"
          alt="Digital Security"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-10" />

        
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}>

            <div className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-8">
              <AlertTriangle className="w-4 h-4" />
              <span>PARE POR 30 SEGUNDOS</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Antes de clicar, transferir ou responder —<br />
              <span className="text-cyan-300">pare por 30 segundos.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-sky-100 mb-6 leading-relaxed">
              Isso pode salvar seu dinheiro.
            </p>

            <p className="text-lg md:text-xl text-sky-200 mb-12 max-w-4xl mx-auto">
              Um guia prático para identificar golpes digitais no momento em que eles acontecem,<br className="hidden md:block" />
              mesmo quando a mensagem parece urgente, pessoal ou confiável.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
              { icon: <MessageSquare className="w-5 h-5" />, text: "WhatsApp" },
              { icon: <CreditCard className="w-5 h-5" />, text: "Pix" },
              { icon: <Instagram className="w-5 h-5" />, text: "Instagram" },
              { icon: <FileText className="w-5 h-5" />, text: "Falsos bancos e boletos" }].
              map((item, i) =>
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-lg border border-white/30">

                  {item.icon}
                  <span className="font-medium">{item.text}</span>
                </motion.div>
              )}
            </div>

            <motion.button
              onClick={scrollToCTA}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-orange-500 hover:bg-orange-600 text-white text-xl font-bold px-12 py-5 rounded-full shadow-2xl transition-all duration-300 inline-flex items-center gap-3">

              <span>👉 Quero me proteger agora</span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-6 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://static.lumi.new/48/48aa8305df94ca9585b3cb036b279946.png"
                alt="Pessoa pressionada"
                className="rounded-2xl shadow-2xl" />

            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-sky-900 mb-6">
                Golpes não pegam desinformados.<br />
                <span className="text-orange-500">Pegam pessoas apressadas.</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Você não cai em golpe por falta de inteligência.<br />
                Você cai quando alguém te pressiona, confunde e apressa uma decisão.
              </p>
              <div className="space-y-4 mb-6">
                {[
                "Uma mensagem inesperada",
                "Um tom de urgência",
                "Um pedido \"só dessa vez\"",
                "E a sensação de que resolver rápido é a melhor saída"].
                map((text, i) =>
                <div key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-gray-700">{text}</p>
                  </div>
                )}
              </div>
              <p className="text-xl font-semibold text-sky-900 mb-4">
                Quando você percebe, o dinheiro já saiu da conta.
              </p>
              <p className="text-lg text-orange-600 font-bold flex items-center gap-2">
                <span>👉</span>
                <span>Este ebook existe para quebrar esse ciclo antes do prejuízo.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Belief Break Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-sky-900 to-cyan-800 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            "Mas eu nunca cairia em golpe…"
          </h2>
          <p className="text-2xl text-sky-100 mb-12">
            Quase todo mundo que caiu pensava exatamente isso.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-8">Golpes modernos:</h3>
            <div className="grid md:grid-cols-2 gap-6 text-left mb-8">
              {[
              "usam fotos reais",
              "copiam linguagem de banco",
              "exploram família, medo e vergonha",
              "parecem situações normais do dia a dia"].
              map((text, i) =>
              <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                  <span className="text-sky-100 text-lg">{text}</span>
                </div>
              )}
            </div>
            <p className="text-xl text-white font-semibold">
              Não é falta de atenção.<br />
              <span className="text-orange-300">É engenharia emocional.</span>
            </p>
          </div>
        </div>
      </section>

      {/* P.A.R.E. Method Section */}
      <section className="py-24 px-6 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-sky-900 mb-4">
              Conheça o método <span className="text-orange-500">P.A.R.E.</span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Um processo mental simples para usar antes de qualquer pagamento, código ou resposta urgente.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {paredSteps.map((step, i) =>
            <motion.div
              key={i}
              whileHover={{ y: -8 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-sky-100">

                <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4">
                  {step.letter}
                </div>
                <div className="text-orange-500 mb-4">{step.icon}</div>
                <h3 className="text-2xl font-bold text-sky-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            )}
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-sky-50 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-sky-900 mb-6">Esse método:</h3>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
              { icon: <Zap className="w-6 h-6" />, text: "não depende de tecnologia" },
              { icon: <Smartphone className="w-6 h-6" />, text: "não exige conhecimento técnico" },
              { icon: <Shield className="w-6 h-6" />, text: "funciona em qualquer aplicativo" }].
              map((item, i) =>
              <div key={i} className="flex items-center gap-3">
                  <div className="text-emerald-600">{item.icon}</div>
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </div>
              )}
            </div>
            <p className="text-xl font-bold text-sky-900 flex items-center gap-2">
              <span>👉</span>
              <span>Se você lembra do P.A.R.E., o golpe perde força.</span>
            </p>
          </div>

          <div className="text-center mt-12">
            <motion.button
              onClick={scrollToCTA}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg font-bold px-10 py-4 rounded-full shadow-xl transition-all duration-300 inline-flex items-center gap-3">

              <span>👉 Quero evitar golpes</span>
            </motion.button>
          </div>
        </div>
      </section>

      {/* What's Inside Section */}
      <section className="py-24 px-6 bg-sky-50 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-sky-900 mb-4 text-center">
            O que está dentro do guia
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">Tudo explicado: em linguagem simples, com regras claras, sem termos técnicos, sem terrorismo</p>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, i) =>
            <motion.div
              key={i}
              whileHover={{ x: 8 }}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 flex items-start gap-4">

                <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
                <span className="text-gray-800 text-lg">{feature}</span>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* For Who Section */}
      <section className="py-24 px-6 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-sky-900 mb-12 text-center">
            Para quem é (e para quem não é)
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-emerald-50 rounded-2xl p-8 border-2 border-emerald-200">
              <h3 className="text-2xl font-bold text-emerald-700 mb-6 flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8" />
                Para quem é:
              </h3>
              <div className="space-y-4">
                {forWho.map((item, i) =>
                <div key={i} className="flex items-start gap-3">
                    <span className="text-emerald-600 text-xl">✔</span>
                    <span className="text-gray-700">{item}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-red-50 rounded-2xl p-8 border-2 border-red-200">
              <h3 className="text-2xl font-bold text-red-700 mb-6 flex items-center gap-3">
                <XCircle className="w-8 h-8" />
                Para quem não é:
              </h3>
              <div className="space-y-4">
                {notForWho.map((item, i) =>
                <div key={i} className="flex items-start gap-3">
                    <span className="text-red-600 text-xl">✖</span>
                    <span className="text-gray-700">{item}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-sky-900 to-cyan-800 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            Este material existe porque golpes<br />estão cada vez mais comuns
          </h2>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 mb-12 border border-white/20">
            <p className="text-xl text-sky-100 mb-6">Todos os dias surgem novos relatos de:</p>
            <div className="grid md:grid-cols-2 gap-4 text-left mb-6">
              {[
              "Pix enviados por engano",
              "Mensagens de \"filhos\" com número novo",
              "Falsos atendentes de banco",
              "Compras em lojas falsas"].
              map((text, i) =>
              <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-400 rounded-full" />
                  <span className="text-sky-100">{text}</span>
                </div>
              )}
            </div>
            <p className="text-xl text-white font-semibold">
              Este ebook reúne os padrões mais recorrentes em um só lugar,<br />
              para você não aprender pelo prejuízo.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-sky-900 mb-6">O que torna este guia diferente</h3>
            <div className="grid md:grid-cols-2 gap-6 text-left mb-8">
              {[
              "Não tenta te assustar",
              "Não promete imunidade",
              "Não fala difícil",
              "Não trata o leitor como ingênuo"].
              map((text, i) =>
              <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  <span className="text-gray-700">{text}</span>
                </div>
              )}
            </div>
            <p className="text-xl font-bold text-sky-900">
              Ele faz algo mais útil:<br />
              <span className="text-orange-500 flex items-center justify-center gap-2 mt-2">
                <span>👉</span>
                <span>te ensina a parar, pensar e confirmar.</span>
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Format Section */}
      <section className="py-24 px-6 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-sky-900 mb-12 text-center">
            Formato e Entrega
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {[
            { icon: "📘", title: "Ebook digital (PDF)", desc: "Formato universal" },
            { icon: "📱", title: "Leitura fácil no celular", desc: "Otimizado para mobile" },
            { icon: "⏱", title: "Leitura rápida", desc: "Consulte sempre que precisar" },
            { icon: "🔒", title: "Acesso imediato", desc: "Após a compra" }].
            map((item, i) =>
            <div key={i} className="bg-white rounded-xl p-6 shadow-lg text-center">
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="font-bold text-sky-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Price CTA Section */}
      <section id="cta-section" className="py-24 px-6 bg-gradient-to-br from-orange-500 to-orange-600 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            Quanto vale evitar um prejuízo?
          </h2>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 mb-8 border border-white/20">
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white/10 rounded-xl p-6 border border-white/30">
                <div className="text-4xl mb-3">💸</div>
                <p className="text-lg font-semibold text-white">Um Pix errado</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6 border border-white/30">
                <div className="text-4xl mb-3">🛒</div>
                <p className="text-lg font-semibold text-white">Uma falsa promoção</p>
              </div>
              <div className="bg-white/10 rounded-xl p-6 border border-white/30">
                <div className="text-4xl mb-3">⚠️</div>
                <p className="text-lg font-semibold text-white">Um golpe simples</p>
              </div>
            </div>
            <p className="text-2xl font-bold text-white">
              Costumam custar centenas ou milhares de reais.
            </p>
          </div>

          <div className="bg-white rounded-2xl md:p-12 shadow-2xl pt-[32px] pl-[32px] pr-[32px] pb-[32px]">
            <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
              <div className="text-center md:text-left">
                <p className="text-2xl text-sky-900 font-bold mb-4">
                  O Guia Anti-Golpe Digital custa:
                </p>
                <div className="text-6xl md:text-7xl font-bold text-orange-600 mb-6">
                  R$27
                </div>
                <p className="text-xl text-emerald-600 font-semibold mb-2">acesso imediato</p>
              </div>
              <div className="text-center md:text-left">
                <div className="space-y-2 text-gray-700">
                  <p className="font-medium">Pagamento único.</p>
                  <p className="font-medium">Sem assinatura.</p>
                  <p className="font-medium">Acesso vitalício ao conteúdo.</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <motion.a
                href="https://pay.kiwify.com.br/FFX6AT1"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => (window as any).fbq?.('track', 'InitiateCheckout')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-emerald-500 hover:bg-emerald-600 text-white text-xl font-bold px-12 py-5 rounded-full shadow-2xl transition-all duration-300 inline-flex items-center gap-3">

                <span>👉 Quero me proteger agora</span>
              </motion.a>
            </div>
          </div>

          <div className="mt-6 space-y-1 md:space-y-0 md:grid md:grid-cols-3 md:gap-6 text-white/90 text-lg">
            <p>Menos que um almoço fora.</p>
            <p>Menos que um Pix errado.</p>
            <p>Mais barato que aprender no prejuízo.</p>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-24 px-6 bg-emerald-50 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-3xl mx-auto text-center">
          <Shield className="w-20 h-20 text-emerald-600 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-sky-900 mb-6">
            Risco zero
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Se você ler o guia e achar que ele não te trouxe clareza nenhuma,<br />
            basta solicitar reembolso.<br />
            <span className="font-bold text-emerald-600">Sem perguntas.</span>
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-sky-900 mb-12 text-center">
            Perguntas Frequentes
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, i) =>
            <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left p-6 flex items-center justify-between hover:bg-sky-50 transition-colors duration-200">

                  <span className="font-bold text-sky-900 text-lg pr-4">{faq.question}</span>
                  <ChevronDown
                  className={`w-6 h-6 text-sky-600 flex-shrink-0 transition-transform duration-300 ${
                  openFaq === i ? "rotate-180" : ""}`
                  } />

                </button>
                <div
                className={`overflow-hidden transition-all duration-300 ${
                openFaq === i ? "max-h-96" : "max-h-0"}`
                }>

                  <p className="p-6 pt-0 text-gray-700">{faq.answer}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-sky-900 via-cyan-800 to-sky-900 fade-in-section opacity-0 transition-all duration-1000">
        <div className="max-w-4xl mx-auto text-center">
          <img
            src="https://static.lumi.new/ce/ce5fba64f35ed32ba4a680f4450dee92.png"
            alt="Pessoa calma e confiante"
            className="w-64 h-64 object-cover rounded-full mx-auto mb-8 shadow-2xl border-8 border-white/20" />

          
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Golpistas vivem de pressa.
          </h2>
          <p className="text-3xl md:text-5xl font-bold text-cyan-300 mb-8">
            Você pode viver de calma.
          </p>
          
          <p className="text-2xl md:text-3xl text-white font-semibold mb-12">
            Acesso por R$27. Comece agora.
          </p>

          <motion.a
            href="https://pay.kiwify.com.br/FFX6AT1"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => (window as any).fbq?.('track', 'InitiateCheckout')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-orange-500 hover:bg-orange-600 text-white text-xl font-bold px-12 py-5 rounded-full shadow-2xl transition-all duration-300 inline-flex items-center gap-3">

            <span>👉 Clique aqui e tenha o guia agora</span>
          </motion.a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-sky-950 text-sky-200 py-12 px-6 text-center">
        <p className="text-sm">
          © {new Date().getFullYear()} Guia Anti-Golpe. Todos os direitos reservados.
        </p>
      </footer>
    </div>);

}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/finaldeano" element={<FinalDeAno />} />
        <Route path="/download" element={<DownloadPage />} />
      </Routes>
    </BrowserRouter>);

}

export default App;